import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Phone, Mail, Globe, MapPin, Send, FileText, ArrowUpRight } from "lucide-react";
import { WhatsAppIcon } from "./WhatsApp";
import { CONTACTS } from "@/content";
import PageBackground from "./PageBackground";
import { useLanguage, validationMessage } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { nome: "", cognome: "", email: "", telefono: "", messaggio: "", privacy: false };

const inputCls =
    "w-full border border-sky-400 bg-transparent px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 outline-none transition-colors focus:border-sky-300 focus:ring-1 focus:ring-sky-400/40";
const labelCls = "mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-sky-400";

function InfoRow({ icon: Icon, label, value, href, testid }) {
    const inner = (
        <>
            <Icon className="h-5 w-5 shrink-0 text-sky-400" />
            <span>
                <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-sky-400">
                    {label}
                </span>
                <span className="mt-1 block font-medium text-slate-100">{value}</span>
            </span>
        </>
    );
    const cls = "flex items-center gap-4 p-4 transition-colors";
    return href ? (
        <a href={href} data-testid={testid} className={`${cls} hover:bg-navy-800`}>
            {inner}
        </a>
    ) : (
        <div data-testid={testid} className={cls}>
            {inner}
        </div>
    );
}

export default function ContactPage() {
    const { t, lang, setLang } = useLanguage();
    const cp = t.contactPage;
    const [form, setForm] = useState(EMPTY);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const set = (key) => (e) => {
        const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (!form.privacy) {
            setError(cp.errorPrivacy);
            return;
        }
        setSending(true);
        try {
            const payload = {
                ...form,
                nome: form.nome.trim(),
                cognome: form.cognome.trim(),
                email: form.email.trim(),
                telefono: form.telefono.trim(),
                messaggio: form.messaggio.trim(),
            };
            await axios.post(`${API}/contact`, payload);
            toast.success(cp.toastTitle, { description: cp.toastDesc });
            setForm(EMPTY);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            setError(
                Array.isArray(detail)
                    ? validationMessage(detail, t)
                    : typeof detail === "string"
                      ? detail
                      : cp.errorGeneric
            );
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="min-h-screen" data-testid="contact-page">
            <PageBackground />
            <header className="relative z-10 border-b border-navy-600">
                <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-6 lg:px-10">
                    <a href="/" data-testid="contactpage-logo" className="flex items-center gap-2.5">
                        <span className="h-2.5 w-2.5 bg-sky-400" aria-hidden="true" />
                        <span className="font-display text-base font-bold tracking-tight text-slate-50">
                            CAPRI GIANLUCA
                        </span>
                    </a>
                    <div className="flex items-center gap-4">
                        <div className="flex items-center border border-navy-600" data-testid="contactpage-lang-toggle">
                            {["it", "en"].map((l) => (
                                <button
                                    key={l}
                                    data-testid={`contactpage-lang-${l}`}
                                    onClick={() => setLang(l)}
                                    className={`px-3 py-2 font-mono text-[11px] font-bold uppercase tracking-widest transition-colors ${
                                        lang === l ? "bg-sky-400 text-white" : "text-slate-400 hover:text-sky-400"
                                    }`}
                                >
                                    {l}
                                </button>
                            ))}
                        </div>
                        <a
                            href="/"
                            data-testid="contactpage-back"
                            className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-slate-400 transition-colors hover:text-sky-400"
                        >
                            <ArrowLeft className="h-4 w-4" />
                            {cp.back}
                        </a>
                    </div>
                </div>
            </header>

            <main className="relative z-10 mx-auto max-w-7xl px-6 py-16 lg:px-10 lg:py-24">
                <p className="font-mono text-xs font-medium uppercase tracking-[0.3em] text-sky-400">{cp.eyebrow}</p>
                <h1 className="mt-4 font-display text-3xl font-bold tracking-tight text-slate-50 sm:text-4xl lg:text-5xl">
                    {cp.title}
                </h1>

                <div className="mt-14 grid gap-10 lg:grid-cols-5">
                    <div className="lg:col-span-2">
                        <div className="border border-navy-600 p-6 sm:p-8" data-testid="contactpage-card">
                            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-sky-400">{cp.role}</p>
                            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-50">
                                {cp.name}
                            </h2>

                            <div className="mt-8 divide-y divide-navy-600 border border-navy-600">
                                <InfoRow icon={MapPin} label={cp.sedeLabel} value={CONTACTS.address} testid="contactpage-sede" />
                                <InfoRow icon={FileText} label={cp.pivaLabel} value={CONTACTS.piva} testid="contactpage-piva" />
                                <InfoRow
                                    icon={Phone}
                                    label={cp.phoneLabel}
                                    value={CONTACTS.phoneDisplay}
                                    href={CONTACTS.phoneHref}
                                    testid="contactpage-phone"
                                />
                                <InfoRow
                                    icon={Mail}
                                    label={cp.emailLabel}
                                    value={CONTACTS.email}
                                    href={`mailto:${CONTACTS.email}`}
                                    testid="contactpage-email"
                                />
                                <InfoRow
                                    icon={Globe}
                                    label={cp.webLabel}
                                    value={CONTACTS.website}
                                    href={CONTACTS.websiteHref}
                                    testid="contactpage-web"
                                />
                            </div>

                            <a
                                href={CONTACTS.whatsapp}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="contactpage-whatsapp"
                                className="mt-6 flex w-full items-center justify-center gap-2 bg-[#25D366] px-6 py-4 font-mono text-xs font-bold uppercase tracking-widest text-white transition-colors hover:bg-[#1EBE5B]"
                            >
                                <WhatsAppIcon className="h-4 w-4" />
                                {cp.whatsappCta}
                                <ArrowUpRight className="h-4 w-4" />
                            </a>
                        </div>
                    </div>

                    <div className="lg:col-span-3">
                        <form
                            onSubmit={submit}
                            data-testid="contactpage-form"
                            className="border border-navy-600 p-6 sm:p-8"
                        >
                            <h2 className="font-display text-xl font-semibold tracking-tight text-slate-50">
                                {cp.formTitle}
                            </h2>
                            <div className="mt-6 grid gap-5 sm:grid-cols-2">
                                <div>
                                    <label htmlFor="cp-nome" className={labelCls}>
                                        {cp.nameField}
                                    </label>
                                    <input
                                        id="cp-nome"
                                        data-testid="contactpage-name-input"
                                        className={inputCls}
                                        value={form.nome}
                                        onChange={set("nome")}
                                        required
                                        placeholder={cp.namePlaceholder}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="cp-cognome" className={labelCls}>
                                        {cp.surnameField}
                                    </label>
                                    <input
                                        id="cp-cognome"
                                        data-testid="contactpage-surname-input"
                                        className={inputCls}
                                        value={form.cognome}
                                        onChange={set("cognome")}
                                        required
                                        placeholder={cp.surnamePlaceholder}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="cp-email" className={labelCls}>
                                        {cp.emailField}
                                    </label>
                                    <input
                                        id="cp-email"
                                        type="email"
                                        data-testid="contactpage-email-input"
                                        className={inputCls}
                                        value={form.email}
                                        onChange={set("email")}
                                        required
                                        placeholder={cp.emailPlaceholder}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="cp-telefono" className={labelCls}>
                                        {cp.phoneField}
                                    </label>
                                    <input
                                        id="cp-telefono"
                                        type="tel"
                                        data-testid="contactpage-phone-input"
                                        className={inputCls}
                                        value={form.telefono}
                                        onChange={set("telefono")}
                                        placeholder="+39 ..."
                                    />
                                </div>
                                <div className="sm:col-span-2">
                                    <label htmlFor="cp-messaggio" className={labelCls}>
                                        {cp.messageField}
                                    </label>
                                    <textarea
                                        id="cp-messaggio"
                                        data-testid="contactpage-message-textarea"
                                        className={`${inputCls} min-h-[130px] resize-y`}
                                        value={form.messaggio}
                                        onChange={set("messaggio")}
                                        required
                                        placeholder={cp.messagePlaceholder}
                                    />
                                </div>
                            </div>

                            <div className="mt-6 flex items-start gap-3">
                                <input
                                    id="cp-privacy-check"
                                    type="checkbox"
                                    data-testid="contactpage-privacy-checkbox"
                                    checked={form.privacy}
                                    onChange={set("privacy")}
                                    className="mt-0.5 h-4 w-4 border-navy-600 bg-navy-950 accent-sky-400"
                                />
                                <label htmlFor="cp-privacy-check" className="cursor-pointer text-sm text-slate-400">
                                    {cp.privacyA}
                                    <a
                                        href="/privacy"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="contactpage-privacy-link"
                                        className="text-sky-400 underline underline-offset-2 transition-colors hover:text-sky-300"
                                    >
                                        {cp.privacyLink}
                                    </a>
                                    {cp.privacyB}
                                </label>
                            </div>

                            {error && (
                                <p data-testid="contactpage-form-error" className="mt-4 text-sm text-red-400">
                                    {error}
                                </p>
                            )}

                            <button
                                type="submit"
                                data-testid="contactpage-submit-button"
                                disabled={sending}
                                className="mt-7 inline-flex w-full items-center justify-center gap-2 bg-sky-400 px-7 py-4 font-mono text-xs font-bold uppercase tracking-widest text-white transition-colors hover:bg-sky-500 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                            >
                                {sending ? cp.sending : cp.submit}
                                <Send className="h-4 w-4" />
                            </button>
                        </form>
                    </div>
                </div>

                <div className="mt-14" data-testid="contactpage-map-wrap">
                    <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">
                        {cp.mapLabel} — {CONTACTS.address}
                    </p>
                    <iframe
                        title="Mappa — Via S. Pertini 5, Sirolo (AN)"
                        data-testid="contactpage-map"
                        src="https://www.openstreetmap.org/export/embed.html?bbox=13.5734%2C43.4941%2C13.6134%2C43.5181&layer=mapnik&marker=43.5061%2C13.5934"
                        className="h-[380px] w-full"
                        style={{
                            WebkitMaskImage:
                                "radial-gradient(ellipse 90% 88% at center, black 55%, transparent 100%)",
                            maskImage:
                                "radial-gradient(ellipse 90% 88% at center, black 55%, transparent 100%)",
                        }}
                        loading="lazy"
                    />
                </div>
            </main>

            <footer className="relative z-10 border-t border-navy-600 py-8 text-center font-mono text-[11px] text-slate-600">
                {t.footer.rights}
            </footer>
        </div>
    );
}
